import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { ScrollArea, ScrollBar } from "./ui/scroll-area";
import { Button } from "./ui/button";
import { FeaturedRestaurantCard } from "./featured/FeaturedRestaurantCard";
import { useFeaturedRestaurants } from "@/hooks/useFeaturedRestaurants";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState, useRef, useCallback, memo } from "react";

const ScrollControls = memo(({ onScroll, canScrollLeft, canScrollRight }: {
  onScroll: (direction: 'left' | 'right') => void;
  canScrollLeft: boolean;
  canScrollRight: boolean;
}) => (
  <div className="flex gap-2">
    <Button
      variant="outline"
      size="icon"
      className="h-8 w-8"
      onClick={() => onScroll('left')}
      disabled={!canScrollLeft}
    >
      <ChevronLeft className="h-4 w-4" />
    </Button>
    <Button
      variant="outline"
      size="icon"
      className="h-8 w-8"
      onClick={() => onScroll('right')}
      disabled={!canScrollRight}
    >
      <ChevronRight className="h-4 w-4" />
    </Button>
  </div>
));

ScrollControls.displayName = 'ScrollControls';

export const FeaturedRestaurants = () => {
  const isMobile = useIsMobile();
  const { data: featuredRestaurants, isLoading } = useFeaturedRestaurants();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || !featuredRestaurants || featuredRestaurants.length <= 3) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  }, [featuredRestaurants]);

  const scroll = useCallback((direction: 'left' | 'right') => {
    if (!scrollContainerRef.current) return;
    
    const scrollAmount = direction === 'left' ? -400 : 400;
    scrollContainerRef.current.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-4 pt-4 md:pt-6">
        <h2 className={`font-semibold ${isMobile ? 'text-lg px-2' : 'text-2xl'}`}>
          Featured Venues
        </h2>
        <ScrollArea className="w-full whitespace-nowrap">
          <div className="flex w-full space-x-3 md:space-x-4 pb-4 px-4">
            {[1, 2].map((i) => (
              <div key={i} className={`flex-none ${isMobile ? 'w-[280px]' : 'w-[320px]'}`}>
                <div className="h-24 bg-accent animate-pulse rounded-lg" />
              </div>
            ))}
          </div>
          <ScrollBar orientation="horizontal" className="opacity-0 md:opacity-100" />
        </ScrollArea>
      </div>
    );
  }

  if (!featuredRestaurants || featuredRestaurants.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4 pt-4 md:pt-6">
      <div className="flex items-center justify-between px-4">
        <h2 className={`font-semibold flex items-center gap-2 ${isMobile ? 'text-lg' : 'text-2xl'}`}>
          Featured Venues
          <Star className="h-4 w-4 md:h-5 md:w-5 text-secondary fill-secondary" />
        </h2>
        {!isMobile && featuredRestaurants.length > 3 && (
          <ScrollControls 
            onScroll={scroll}
            canScrollLeft={canScrollLeft}
            canScrollRight={canScrollRight}
          />
        )}
      </div>
      
      <ScrollArea className="w-full whitespace-nowrap">
        <div 
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="flex w-full space-x-3 md:space-x-4 pb-4 px-4"
        >
          {featuredRestaurants.map((restaurant) => (
            <div 
              key={restaurant.id} 
              className={`flex-none ${isMobile ? 'w-[280px]' : 'w-[320px]'}`}
            >
              <FeaturedRestaurantCard restaurant={restaurant} />
            </div>
          ))}
        </div>
        <ScrollBar orientation="horizontal" className="opacity-0 md:opacity-100" />
      </ScrollArea>
      
      {isMobile && featuredRestaurants.length > 1 && (
        <div className="flex justify-center gap-1 pt-2">
          {Array.from({ length: featuredRestaurants.length }).map((_, index) => (
            <div
              key={index}
              className={`h-1 rounded-full transition-all duration-300 ${
                index === 0 ? 'w-4 bg-primary' : 'w-1 bg-primary/30'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};